import { useCallback } from 'react';
import { type SWRInfiniteKeyedMutator } from 'swr/infinite';

import { getApiClient } from '@apps/web/lib/api_client';
import {
    type OrganizationConversationsResponse,
    updateConversationInPages,
} from '@apps/web/swr_data_fetching/conversation_pages';
import {
    type ConversationStatus,
    type OrganizationConversation,
    type OrganizationConversationAuditEvent,
    type OrganizationConversationMetadata,
} from '@libraries/typescript/api_client';

export {
    type ConversationStatus,
    type OrganizationConversationAuditEvent,
} from '@libraries/typescript/api_client';

export type ConversationOwnershipFields = {
    readonly status: ConversationStatus;
    readonly assigned_to_user_id: string | null;
    readonly assigned_to_user_name: string | null;
    readonly assigned_at: string | null;
};

type ConversationPages = Array<OrganizationConversationsResponse>;
type ConversationDetailPages = Array<OrganizationConversation>;

function updateConversationInDetailPages(
    pages: ConversationDetailPages | undefined,
    conversationId: string,
    update: (
        conversation: OrganizationConversation,
    ) => OrganizationConversation,
): ConversationDetailPages {
    return (pages ?? []).map((conversation) =>
        conversation.id === conversationId
            ? update(conversation)
            : conversation,
    );
}

async function mutateConversationOptimistically(
    mutateConversations: SWRInfiniteKeyedMutator<ConversationPages> | undefined,
    mutateConversationDetail:
        | SWRInfiniteKeyedMutator<ConversationDetailPages>
        | undefined,
    conversationId: string,
    optimisticUpdate: (
        conversation: OrganizationConversationMetadata,
    ) => OrganizationConversationMetadata,
    request: () => Promise<{
        readonly conversation: OrganizationConversationMetadata;
    }>,
): Promise<void> {
    if (!mutateConversations && !mutateConversationDetail) {
        await request();
        return;
    }

    // Both caches share one in-flight mutation. The list stores metadata while
    // the thread stores the full conversation response, so the latter merges
    // returned metadata without discarding messages, calls, or timeline rows.
    let requestPromise:
        | Promise<{
              readonly conversation: OrganizationConversationMetadata;
          }>
        | undefined;
    const runRequest = () => {
        requestPromise ??= request();
        return requestPromise;
    };
    const cacheMutations: Array<Promise<unknown>> = [];

    if (mutateConversations) {
        cacheMutations.push(
            mutateConversations(
                async (pages) => {
                    const response = await runRequest();
                    return (
                        updateConversationInPages(
                            pages,
                            conversationId,
                            () => response.conversation,
                        ) ?? []
                    );
                },
                {
                    optimisticData: (pages) =>
                        updateConversationInPages(
                            pages,
                            conversationId,
                            optimisticUpdate,
                        ) ?? [],
                    rollbackOnError: true,
                    revalidate: true,
                },
            ),
        );
    }

    if (mutateConversationDetail) {
        cacheMutations.push(
            mutateConversationDetail(
                async (pages) => {
                    const response = await runRequest();
                    return updateConversationInDetailPages(
                        pages,
                        conversationId,
                        (conversation) => ({
                            ...conversation,
                            ...response.conversation,
                        }),
                    );
                },
                {
                    optimisticData: (pages) =>
                        updateConversationInDetailPages(
                            pages,
                            conversationId,
                            (conversation) => ({
                                ...conversation,
                                ...optimisticUpdate(conversation),
                            }),
                        ),
                    rollbackOnError: true,
                    revalidate: true,
                },
            ),
        );
    }

    await Promise.all(cacheMutations);
}

export function readConversationOwnership(
    conversation: OrganizationConversationMetadata,
): ConversationOwnershipFields {
    return {
        status: conversation.status,
        assigned_to_user_id: conversation.assigned_to_user_id ?? null,
        assigned_to_user_name: conversation.assigned_to_user_name ?? null,
        assigned_at: conversation.assigned_at ?? null,
    };
}

export function readConversationAuditEvents(
    conversation: OrganizationConversation | null | undefined,
): ReadonlyArray<OrganizationConversationAuditEvent> {
    return conversation?.audit_events ?? [];
}

export function useConversationOwnershipActions(
    organizationId: string | null,
    mutateConversations?: SWRInfiniteKeyedMutator<ConversationPages>,
    mutateConversationDetail?: SWRInfiniteKeyedMutator<ConversationDetailPages>,
) {
    const assign = useCallback(
        async (conversationId: string, assignedToUserId: string | null) => {
            if (!organizationId) {
                return;
            }
            const assignedAt = new Date().toISOString();
            await mutateConversationOptimistically(
                mutateConversations,
                mutateConversationDetail,
                conversationId,
                (conversation) => ({
                    ...conversation,
                    assigned_to_user_id: assignedToUserId,
                    assigned_to_user_name:
                        assignedToUserId === null
                            ? null
                            : conversation.assigned_to_user_id ===
                                assignedToUserId
                              ? conversation.assigned_to_user_name
                              : null,
                    assigned_at: assignedToUserId === null ? null : assignedAt,
                }),
                () =>
                    getApiClient().patch({
                        url: '/organizations/{organization_id}/conversations/{conversation_id}/assignment',
                        pathParams: {
                            organization_id: organizationId,
                            conversation_id: conversationId,
                        },
                        body: { assigned_to_user_id: assignedToUserId },
                        reason: 'conversation-update-assignment',
                    }),
            );
        },
        [organizationId, mutateConversations, mutateConversationDetail],
    );

    const close = useCallback(
        async (conversationId: string) => {
            if (!organizationId) {
                return;
            }
            await mutateConversationOptimistically(
                mutateConversations,
                mutateConversationDetail,
                conversationId,
                (conversation) => ({
                    ...conversation,
                    status: 'closed',
                }),
                () =>
                    getApiClient().post({
                        url: '/organizations/{organization_id}/conversations/{conversation_id}/close',
                        pathParams: {
                            organization_id: organizationId,
                            conversation_id: conversationId,
                        },
                        reason: 'conversation-close',
                    }),
            );
        },
        [organizationId, mutateConversations, mutateConversationDetail],
    );

    const reopen = useCallback(
        async (conversationId: string) => {
            if (!organizationId) {
                return;
            }
            await mutateConversationOptimistically(
                mutateConversations,
                mutateConversationDetail,
                conversationId,
                (conversation) => ({
                    ...conversation,
                    status: 'open',
                }),
                () =>
                    getApiClient().post({
                        url: '/organizations/{organization_id}/conversations/{conversation_id}/reopen',
                        pathParams: {
                            organization_id: organizationId,
                            conversation_id: conversationId,
                        },
                        reason: 'conversation-reopen',
                    }),
            );
        },
        [organizationId, mutateConversations, mutateConversationDetail],
    );

    return { assign, close, reopen };
}
